import React, { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { DatePick, MonthPick, TimePick } from "@/components/erp/DatePick";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {action && <div className="w-full sm:w-auto max-w-full [&>div]:flex-wrap">{action}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white border border-slate-200 rounded-2xl ${className}`} {...rest}>{children}</div>;
}

export function StatCard({ icon: Icon, label, value, accent = "text-slate-900", sub }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase text-slate-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className={`font-head text-2xl font-bold mt-2 ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </Card>
  );
}

const STATUS_COLORS = {
  NEW: "bg-sky-100 text-sky-700", "UP COMING": "bg-emerald-100 text-emerald-700 font-bold", REVIEWING: "bg-amber-100 text-amber-700",
  QUOTED: "bg-indigo-100 text-indigo-700", APPROVED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-rose-100 text-rose-700", COMPLETED: "bg-emerald-100 text-emerald-700",
  IN_PROGRESS: "bg-sky-100 text-sky-700", WAITING: "bg-slate-100 text-slate-600",
  ACTIVE: "bg-emerald-100 text-emerald-700", CONFIRMED: "bg-emerald-100 text-emerald-700",
  RESCHEDULED: "bg-amber-100 text-amber-700", AVAILABLE: "bg-emerald-100 text-emerald-700",
  RUNNING: "bg-sky-100 text-sky-700", MAINTENANCE: "bg-amber-100 text-amber-700",
  HIGH: "bg-rose-100 text-rose-700", MEDIUM: "bg-amber-100 text-amber-700", LOW: "bg-slate-100 text-slate-600",
  Present: "bg-emerald-100 text-emerald-700", Active: "bg-emerald-100 text-emerald-700",
  BREAKDOWN: "bg-rose-100 text-rose-700", OFFLINE: "bg-slate-200 text-slate-600",
  SKIPPED: "bg-slate-100 text-slate-400", REWORK: "bg-rose-100 text-rose-700",
  ORDERED: "bg-indigo-100 text-indigo-700", RECEIVED: "bg-emerald-100 text-emerald-700",
  OPEN: "bg-sky-100 text-sky-700", BACKORDER: "bg-amber-100 text-amber-700", CONVERTED: "bg-indigo-100 text-indigo-700",
  PAID: "bg-emerald-100 text-emerald-700", PARTIAL: "bg-amber-100 text-amber-700", UNPAID: "bg-rose-100 text-rose-700",
  DRAFT: "bg-slate-200 text-slate-700",
  DISPATCHED: "bg-sky-100 text-sky-700",
};

export function Badge({ children }) {
  const cls = STATUS_COLORS[children] || "bg-slate-100 text-slate-600";
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${cls}`}>{children}</span>;
}

const rowText = (r) => { try { return JSON.stringify(r).toLowerCase(); } catch { return ""; } };

export function usePaged(rows, sizes = [20, 40, 80]) {
  const [size, setSize] = useState(sizes[0]);
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const cur = Math.min(page, pages);
  useEffect(() => { setPage(1); }, [rows.length, size]);
  return { size, setSize, page: cur, setPage, pages, sizes, total: rows.length, slice: rows.slice((cur - 1) * size, cur * size) };
}

export function Pager({ p, testid = "pager", unit = "baris" }) {
  if (p.total <= p.sizes[0]) return null;
  const from = (p.page - 1) * p.size + 1, to = Math.min(p.total, p.page * p.size);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500" data-testid={testid}>
      <span className="flex items-center gap-1.5">Tampilkan
        <select data-testid={`${testid}-size`} value={p.size} onChange={(e) => p.setSize(+e.target.value)} className="border border-slate-300 rounded-md px-1.5 py-1 bg-white">
          {p.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select> {unit} · {from}–{to} dari {p.total}
      </span>
      <span className="flex items-center gap-1">
        <button type="button" data-testid={`${testid}-prev`} disabled={p.page <= 1} onClick={() => p.setPage(p.page - 1)} className="px-2.5 py-1 rounded-md border border-slate-300 disabled:opacity-40 hover:bg-slate-50">‹ Prev</button>
        <span className="px-2 font-medium text-slate-700" data-testid={`${testid}-page`}>{p.page} / {p.pages}</span>
        <button type="button" data-testid={`${testid}-next`} disabled={p.page >= p.pages} onClick={() => p.setPage(p.page + 1)} className="px-2.5 py-1 rounded-md border border-slate-300 disabled:opacity-40 hover:bg-slate-50">Next ›</button>
      </span>
    </div>
  );
}

export function SearchBox({ value, onChange, testid = "table-search", placeholder = "Cari…" }) {
  return <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full sm:w-72 border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200" />;
}

export function Table({ columns, rows, renderRow, empty = "Belum ada data", testid, search = true }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => { const s = q.trim().toLowerCase(); return s ? rows.filter((r) => rowText(r).includes(s)) : rows; }, [rows, q]);
  const p = usePaged(shown);
  return (
    <Card className="overflow-hidden">
      {search && rows.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100">
          <SearchBox value={q} onChange={setQ} testid={`${testid || "table"}-search`} />
          {q && <span className="text-xs text-slate-400 shrink-0">{shown.length} hasil</span>}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {columns.map((c) => <th key={c} className="text-left font-medium text-slate-500 px-4 py-3 whitespace-nowrap">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center text-slate-400 py-12">{q ? "Tidak ada hasil pencarian" : empty}</td></tr>
            ) : p.slice.map(renderRow)}
          </tbody>
        </table>
      </div>
      <Pager p={p} testid={`${testid || "table"}-pager`} />
    </Card>
  );
}

export function Modal({ open, onClose, title, children, footer, wide, xwide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${xwide ? "max-w-6xl" : wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-head text-lg font-semibold">{title}</h3>
          {onClose && <button onClick={onClose} className="text-slate-400 hover:text-slate-900">✕</button>}
        </div>
        <div className="p-6">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function Input(props) {
  const [show, setShow] = useState(false);
  const cls = `w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 ${props.className || ""}`;
  if (props.type === "month" || props.type === "time") {
    const P = props.type === "month" ? MonthPick : TimePick;
    return <P value={props.value} min={props.min} max={props.max} disabled={props.disabled} className={props.type === "time" ? "" : (props.className || "").replace(/!/g, "").replace(/\b(border\S*|rounded\S*|px-\S+|py-\S+)/g, "")} testid={props["data-testid"]}
      onChange={(v) => props.onChange?.({ target: { value: v }, currentTarget: { value: v } })} />;
  }
  if (props.type === "date") {
    const { value, onChange, min, max, disabled, className, placeholder } = props;
    return <DatePick value={value} min={min} max={max} disabled={disabled} className={(className || "").replace(/!/g, "")} placeholder={placeholder} testid={props["data-testid"]}
      onChange={(v) => onChange?.({ target: { value: v }, currentTarget: { value: v } })} />;
  }
  if (props.type !== "password") return <input {...props} className={cls} />;
  const tid = props["data-testid"] || "password";
  return (
    <div className={`relative ${(props.className || "").includes("!w-") ? "w-fit" : "w-full"}`}>
      <input {...props} type={show ? "text" : "password"} className={`${cls} pr-10`} />
      <button type="button" tabIndex={-1} data-testid={`${tid}-toggle`} aria-label={show ? "Sembunyikan" : "Lihat"} onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export function Select({ children, className = "", ...props }) {
  return <select {...props} className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 ${className}`}>{children}</select>;
}

export function Btn({ children, variant = "primary", ...props }) {
  const styles = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    ghost: "border border-slate-300 hover:border-slate-900",
    danger: "bg-rose-500 text-white hover:bg-rose-400",
    accent: "bg-sky-500 text-white hover:bg-sky-400",
  };
  return <button {...props} className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${styles[variant]} ${props.className || ""}`}>{children}</button>;
}

export function useDirtyGuard() {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const snapRef = React.useRef(null);
  return {
    confirmOpen,
    setConfirmOpen,
    snapshot: (v) => { snapRef.current = JSON.stringify(v ?? null); },
    clear: () => { snapRef.current = null; setConfirmOpen(false); },
    attemptClose: (v, closeFn) => {
      if (snapRef.current !== null && JSON.stringify(v ?? null) !== snapRef.current) setConfirmOpen(true);
      else closeFn();
    },
  };
}

export function ConfirmClose({ open, onSave, onDiscard, onCancel, testid = "confirm-close" }) {
  if (!open) return null;
  return (
    <div data-testid={testid} className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-head text-lg font-semibold">Perubahan Belum Disimpan</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">Ada perubahan yang belum disimpan. Simpan atau buang perubahan sebelum keluar?</p>
        <div className="flex flex-col gap-2">
          <Btn data-testid={`${testid}-save`} onClick={onSave}>Simpan</Btn>
          <Btn variant="danger" data-testid={`${testid}-discard`} onClick={onDiscard}>Tidak Simpan (buang perubahan)</Btn>
          <Btn variant="ghost" data-testid={`${testid}-cancel`} onClick={onCancel}>Batal (kembali mengedit)</Btn>
        </div>
      </div>
    </div>
  );
}
